// src/services/pdf/sections/GPActionsSection.ts
// Renders Section 3: Auto-generated GP actions mapped to NICE guidelines.

import { PDFDocument } from "../core/PDFDocument";
import { PDF_COLORS } from "../core/colors";
import { PAGE_LAYOUT } from "../core/layout";
import type { PDFRenderData, GPAction, FlagLevel } from "../types";
import type { SectionRenderer } from "./SectionRenderer";
import type { RGBColor } from "../core/colors";

const URGENCY_STYLES: Record<FlagLevel, { bg: RGBColor; text: RGBColor; label: string }> = {
  red: { bg: PDF_COLORS.bgRedTint, text: PDF_COLORS.red, label: "Act today" },
  amber: { bg: PDF_COLORS.bgAmberTint, text: PDF_COLORS.amber, label: "Before next appt" },
  green: { bg: PDF_COLORS.bgGreenTint, text: PDF_COLORS.green, label: "Guidance" },
};

export class GPActionsSection implements SectionRenderer {
  readonly id = "gpActions";

  render(doc: PDFDocument, data: PDFRenderData): void {
    const { gpActions } = data;
    if (gpActions.length === 0) return;

    doc.ensureSpace(15);
    doc.drawHorizontalRule();

    doc.setFont("bold", PAGE_LAYOUT.fontSize.sectionTitle);
    doc.setColor(PDF_COLORS.brandPrimary);
    doc.text("GP ACTIONS TODAY", doc.contentLeft);
    doc.advanceY(2);

    doc.setFont("italic", PAGE_LAYOUT.fontSize.tiny);
    doc.setColor(PDF_COLORS.textMuted);
    doc.text("Auto-generated from 6-week data. Max 6 items, colour-coded by urgency.", doc.contentLeft);
    doc.advanceY(5);

    // Render actions grouped by urgency
    const redActions = gpActions.filter((a) => a.urgency === "red");
    const amberActions = gpActions.filter((a) => a.urgency === "amber");
    const greenActions = gpActions.filter((a) => a.urgency === "green");

    for (const action of [...redActions, ...amberActions, ...greenActions]) {
      this.renderAction(doc, action);
    }

    doc.advanceY(PAGE_LAYOUT.subsectionGap);
  }

  private renderAction(doc: PDFDocument, action: GPAction): void {
    const style = URGENCY_STYLES[action.urgency];
    const rowHeight = 8;

    doc.ensureSpace(rowHeight + 2);
    const startY = doc.y;

    // Background stripe
    doc.drawRect(
      doc.contentLeft, startY - 1.5,
      doc.contentWidth, rowHeight,
      { fill: style.bg, radius: 1.5 }
    );

    // Vertically center text within the rectangle
    const textY = startY + rowHeight / 2 - 1;
    doc.y = textY;

    // Urgency indicator dot
    doc.drawCircle(doc.contentLeft + 3, textY, 1.5, style.text);

    // Action text
    doc.setFont("bold", PAGE_LAYOUT.fontSize.small);
    doc.setColor(style.text);
    doc.text(action.action, doc.contentLeft + 8, { maxWidth: doc.contentWidth - 45 });

    // NICE reference (right-aligned)
    if (action.niceRef) {
      doc.setFont("normal", PAGE_LAYOUT.fontSize.tiny);
      doc.setColor(PDF_COLORS.textMuted);
      const refWidth = doc.getTextWidth(action.niceRef);
      doc.text(action.niceRef, doc.contentRight - refWidth - 2);
    }

    doc.y = startY;
    doc.advanceY(rowHeight + 1);
  }
}
